import {
  Clock3,
  Heart,
  Home,
  LibraryBig,
  ListMusic,
  Search,
  Settings,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { focusFirst, useTvNavigation } from '../lib/tv-navigation';
import { useAppState } from '../state/app-state';
import { EmptyState } from './empty-state';
import { TvButton } from './tv-button';

const navigation = [
  { to: '/', label: '首页', Icon: Home },
  { to: '/search', label: '搜索', Icon: Search },
  { to: '/library', label: '曲库', Icon: LibraryBig },
  { to: '/favorites', label: '收藏', Icon: Heart },
  { to: '/queue', label: '点歌台', Icon: ListMusic },
  { to: '/history', label: '历史', Icon: Clock3 },
  { to: '/settings', label: '设置', Icon: Settings },
];

const titles: Record<string, string> = {
  '/': '晚上好，准备开唱',
  '/search': '搜索歌曲',
  '/library': '全部曲库',
  '/favorites': '我的收藏',
  '/queue': '点歌台',
  '/history': '演唱历史',
  '/settings': '曲库与设备',
};

export function AppShell() {
  const { data, loading, error, refresh, selectedSinger, selectSinger } = useAppState();
  const location = useLocation();
  const navigate = useNavigate();
  const [time, setTime] = useState(() => new Date());
  const onBack = useCallback(() => {
    if (location.pathname !== '/') navigate(-1);
  }, [location.pathname, navigate]);
  useTvNavigation(onBack);

  useEffect(() => {
    const timer = window.setInterval(() => setTime(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    focusFirst();
  }, [location.pathname]);

  const readyProviders = useMemo(() => data?.providers.filter((provider) => provider.enabled && provider.status === 'ready').length ?? 0, [data]);

  if (loading) {
    return (
      <main className="boot-screen">
        <img src="/brand/pipi-karaoke-mascot.png" alt="皮皮 K 歌" />
        <div><span>皮皮 K 歌</span><h1>正在布置舞台</h1><i /></div>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="fatal-screen">
        <EmptyState
          title="服务暂时没有回应"
          description={error || '请确认皮皮 K 歌服务已经启动。'}
          action={<TvButton tone="primary" onClick={() => void refresh()}>重新连接</TvButton>}
        />
      </main>
    );
  }

  return (
    <div className="app-shell">
      <aside className="side-nav" aria-label="主导航">
        <button type="button" className="brand-button" data-tv-focusable onClick={() => navigate('/')} aria-label="皮皮 K 歌首页">
          <img src="/brand/pipi-karaoke-mascot.png" alt="" />
          <span>皮皮<br />K 歌</span>
        </button>
        <nav>
          {navigation.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} end={to === '/'} data-tv-focusable className={({ isActive }) => isActive ? 'nav-item is-active' : 'nav-item'}>
              <Icon size={25} strokeWidth={2.1} />
              <span>{label}</span>
              {to === '/queue' && data.queue.length > 0 && <b>{data.queue.length}</b>}
            </NavLink>
          ))}
        </nav>
        <div className="side-nav__footer">
          <span className="connection-dot"><Wifi size={16} />{readyProviders}</span>
        </div>
      </aside>
      <div className="app-stage">
        <header className="top-bar">
          <div>
            <span className="eyebrow">PIPI KARAOKE</span>
            <h1>{titles[location.pathname] || '皮皮 K 歌'}</h1>
          </div>
          <div className="top-bar__status">
            <button type="button" data-tv-focusable className="profile-switcher" onClick={() => {
              const index = data.profiles.findIndex((profile) => profile.name === selectedSinger);
              selectSinger(data.profiles[(index + 1) % data.profiles.length]?.name ?? '我们家');
            }}>
              <span className="profile-avatar">皮</span>
              <span><small>当前演唱者</small><strong>{selectedSinger}</strong></span>
            </button>
            <span className="clock"><strong>{time.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}</strong><small>{time.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', weekday: 'short' })}</small></span>
            <span className={`server-state ${readyProviders ? 'is-online' : ''}`} title={`${readyProviders} 个曲库可用`}>
              {readyProviders ? <Wifi size={22} /> : <WifiOff size={22} />}
            </span>
          </div>
        </header>
        <main className="page-content" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
